import { Plus, Sparkles, ChevronRight, Search, LogOut, Building2, User, Menu } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import QuickCreateMenu from "../components/QuickCreateMenu";
import CommandPalette from "../components/CommandPalette";
import SyncStatus from "../components/SyncStatus";
import { useSidebarToggle } from "../hooks/useSidebarToggle";

const pageMap = {
  "/dashboard": "Dashboard",
  "/invoices": "Invoices",
  "/quotations": "Quotations",
  "/clients": "Clients",
  "/products": "Products",
  "/items": "Products",
  "/settings": "Settings",
  "/settings-backup": "Backup Settings",
  "/devtools": "Dev Tools",
};

export default function TopBar() {
  const { session, logout } = useAuth();
  const { toggleMobile } = useSidebarToggle();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const menuRef = useRef(null);
  const plusButtonRef = useRef(null);
  
  const label = pageMap[location.pathname] ?? "Dashboard";
  
  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to login page after logout
      window.location.reload();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };
  
  const currentAccount = session?.accounts?.find(a => a.id === session.accountId);
  
  // For web app MVP: Use user's full name as account name (single account per user)
  const accountDisplayName = currentAccount?.name || session?.user?.fullName || session?.user?.email || 'Account';

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    }

    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAccountMenu]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // ⌘K / Ctrl+K → Command Palette (Search)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      // ⌘N / Ctrl+N → Quick Create (optional)
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        setShowQuickCreate(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Navigation handler for child components
  const handleNavigate = (page, context = null) => {
    // Map old page keys to routes
    const routeMap = {
      dashboard: "/dashboard",
      invoices: "/invoices",
      quotations: "/quotations",
      clients: "/clients",
      products: "/products",
      items: "/items",
      settings: "/settings",
      "settings-backup": "/settings-backup",
      devtools: "/devtools",
    };
    
    const route = routeMap[page] || `/${page}`;
    navigate(route);
  };

  return (
    <header
      className="
        h-14 lg:h-14
        shrink-0
        bg-white/95 backdrop-blur-md
        border-b border-[var(--color-border)]
        px-4 lg:px-6
        flex items-center justify-between gap-3
        sticky top-0
        z-30
      "
    >
      {/* Left Section: Hamburger (Mobile) + Breadcrumb */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile Hamburger Menu */}
        <button
          onClick={toggleMobile}
          className="lg:hidden w-9 h-9 rounded-lg bg-[var(--color-bg-muted)] border border-[var(--color-border)] flex items-center justify-center hover:bg-white transition-colors active:scale-95"
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>

        {/* Breadcrumb / Context */}
        <div className="flex items-center gap-2 text-sm min-w-0">
          <span className="text-[var(--color-text-primary)] font-medium truncate">
            {label}
          </span>
        </div>
      </div>

      {/* Center: Command Bar (Hidden on small mobile, visible on tablet+) */}
      <div className="hidden md:flex flex-1 justify-center max-w-[420px] mx-auto">
        <button
          onClick={() => setShowCommandPalette(true)}
          className="relative w-full group"
        >
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] group-hover:text-[var(--color-text-secondary)] transition-colors pointer-events-none"
          />
          <div
            className="
              w-full
              bg-[var(--color-bg-muted)]
              pl-9 pr-20 py-2
              rounded-lg
              text-sm text-left
              border border-transparent
              group-hover:border-[var(--color-border)]
              group-hover:bg-white
              transition-all
              text-[var(--color-text-muted)]
            "
          >
            Search or jump to...
          </div>
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--color-text-muted)] group-hover:text-[var(--color-text-secondary)] transition-colors pointer-events-none">
            ⌘K
          </span>
        </button>
      </div>

      {/* Right Section: Actions */}
      <div className="flex items-center gap-2 lg:gap-4">
        {/* Search Icon (Mobile only - replaces command bar) */}
        <button
          onClick={() => setShowCommandPalette(true)}
          className="md:hidden w-9 h-9 rounded-lg bg-[var(--color-bg-muted)] border border-[var(--color-border)] flex items-center justify-center hover:bg-white transition-colors active:scale-95"
          aria-label="Search"
        >
          <Search size={16} />
        </button>

        {/* Sync Status Indicator - Hidden on small mobile */}
        <div className="hidden sm:block">
          <SyncStatus />
        </div>

        {/* Quick Create */}
        <button
          ref={plusButtonRef}
          onClick={() => setShowQuickCreate(!showQuickCreate)}
          className={`
            h-9 w-9 lg:w-auto lg:px-2
            rounded-lg
            bg-[var(--color-primary)]
            text-white
            text-sm font-medium
            flex items-center justify-center lg:gap-1.5
            transition-all duration-100
            active:scale-95
            ${showQuickCreate ? 'opacity-100 scale-95' : 'hover:opacity-90'}
          `}
          title="Quick Create (⌘N)"
          aria-label="Quick create"
        >
          <Plus size={20} />
        </button>

        {/* AI Assistant - Hidden on mobile */}
        <button
          className="
            hidden lg:flex
            h-9 w-9
            rounded-lg
            bg-[var(--color-bg-muted)]
            border border-[var(--color-border)]
            items-center justify-center
            text-[var(--color-text-secondary)]
            hover:bg-white
            transition-colors
          "
          title="Ask AI"
        >
          <Sparkles size={15} />
        </button>

        {/* Account Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowAccountMenu(!showAccountMenu)}
            className="
              h-9 w-9 lg:w-auto lg:px-3
              rounded-lg
              bg-[var(--color-bg-muted)]
              border border-[var(--color-border)]
              flex items-center justify-center lg:gap-2
              text-sm
              text-[var(--color-text-primary)]
              hover:bg-white
              transition-colors
              active:scale-95
            "
            aria-label="Account menu"
          >
            <Building2 size={14} />
            <span className="hidden lg:inline max-w-[120px] truncate">{accountDisplayName}</span>
          </button>
          
          {showAccountMenu && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-2xl border border-slate-200 py-1 z-[100]">
              {/* User Info Section */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                    {session?.user?.fullName?.charAt(0)?.toUpperCase() || session?.user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {session?.user?.fullName || 'User'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {session?.user?.email || 'user@example.com'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Current Account */}
              <div className="px-4 py-2 border-b border-slate-100">
                <p className="text-xs text-slate-500 mb-1">Current Account</p>
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-slate-400 shrink-0" />
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {accountDisplayName}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="py-1">
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors active:bg-red-100"
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Create Menu - Lightweight Dropdown */}
      <QuickCreateMenu
        isOpen={showQuickCreate}
        onClose={() => setShowQuickCreate(false)}
        onNavigate={handleNavigate}
        anchorRef={plusButtonRef}
      />

      {/* Command Palette - Full Modal for Search */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onNavigate={handleNavigate}
        currentPage={location.pathname.substring(1) || "dashboard"}
      />
    </header>
  );
}
